'use client';

import { useMemo, useState } from 'react';
import { AlertTriangle, BadgeCheck, Check, Plus, Siren, Trash2, Wallet, X, Zap } from 'lucide-react';
import type { Booking, RentalClient, Vehicle } from '@/types';
import { api, ApiError } from '@/lib/api';
import { currencySymbol, formatMoney } from '@/lib/currency';
import { parseFinePage } from '@/lib/ledger/ledger-catalog';
import { listPath } from '@/lib/query/page';
import { useInvalidateTenantResource } from '@/lib/query/scope';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { StatusChip, fineTone } from './desk-feedback';

export interface FineRecord {
  id: string;
  vehicleId: string;
  date: string;
  label: string;
  amount: number;
  clientName?: string;
  bookingId?: string;
  status: 'unpaid' | 'paid' | 'disputed';
  pvNumber?: string;
}

interface FinesModuleProps {
  vehicles: Vehicle[];
  bookings: Booking[];
  clients: RentalClient[];
  currency?: string | null;
  headerQuery?: string;
}

const LABELS = ['Excès de vitesse', 'Stationnement', 'Feu rouge', 'Autre'];

type StatusFilter = 'all' | FineRecord['status'];

function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

function shortId(): string {
  return Math.random().toString(36).slice(2, 7).toUpperCase();
}

const inputCls =
  'w-full rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand focus:bg-card';
const labelCls = 'mb-1 block text-xs font-semibold text-muted';

function vehicleLabel(vehicles: Vehicle[], id: string): string {
  const v = vehicles.find((x) => x.id === id);
  if (!v) return id;
  return `${v.name} · ${v.licensePlate}`;
}

function statusFr(s: FineRecord['status']): string {
  if (s === 'paid') return 'Payée';
  if (s === 'disputed') return 'Contestée';
  return 'Impayée';
}

export function FinesModule({ vehicles, bookings, clients, currency, headerQuery = '' }: FinesModuleProps) {
  const cur = currency ?? 'MAD';
  const invalidate = useInvalidateTenantResource();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const fineQuery = useTenantQuery(
    ['fines', headerQuery, statusFilter],
    listPath('/api/fines', { perPage: 20, q: headerQuery.trim(), status: statusFilter }),
    parseFinePage,
  );
  const fines = (fineQuery.data?.data ?? []) as FineRecord[];
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Add-form state
  const [fVehicle, setFVehicle] = useState('');
  const [fDate, setFDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [fLabel, setFLabel] = useState(LABELS[0]);
  const [fAmount, setFAmount] = useState('');
  const [fClient, setFClient] = useState('');
  const [fPv, setFPv] = useState(() => `PV-${shortId()}`);

  const unpaid = useMemo(() => fines.filter((f) => f.status === 'unpaid'), [fines]);
  const unpaidTotal = unpaid.reduce((s, f) => s + f.amount, 0);
  const paidThisMonth = useMemo(() => {
    const now = new Date();
    return fines
      .filter((f) => {
        if (f.status !== 'paid') return false;
        const d = new Date(f.date);
        return !isNaN(d.getTime()) && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((s, f) => s + f.amount, 0);
  }, [fines]);

  const filtered = fines;

  async function update(id: string, patch: Partial<FineRecord>) {
    const current = fines.find((fine) => fine.id === id);
    if (!current) return;
    try {
      await api(`/api/fines/${id}`, { method: 'PATCH', body: JSON.stringify({ ...current, ...patch }) });
      await invalidate('fines');
    } catch (error: unknown) {
      window.alert(error instanceof ApiError ? error.message : 'Could not update this fine.');
    }
  }

  function resetForm() {
    setFVehicle('');
    setFDate(new Date().toISOString().slice(0, 10));
    setFLabel(LABELS[0]);
    setFAmount('');
    setFClient('');
    setFPv(`PV-${shortId()}`);
  }

  async function addFine() {
    const amount = Number(fAmount);
    if (!fVehicle || !fDate || !isFinite(amount) || amount < 0) return;
    try {
      await api('/api/fines', {
        method: 'POST',
        body: JSON.stringify({
          vehicleId: fVehicle,
          date: fDate,
          label: fLabel,
          amount,
          clientName: fClient || undefined,
          status: 'unpaid',
          pvNumber: fPv.trim() || `PV-${shortId()}`,
        }),
      });
      await invalidate('fines');
      resetForm();
      setShowAdd(false);
    } catch (error: unknown) {
      window.alert(error instanceof ApiError ? error.message : 'Could not save this fine.');
    }
  }

  /** Find a booking covering vehicle+date → return it for auto-attribution. */
  function suggestBooking(vehicleId: string, date: string): Booking | undefined {
    if (!vehicleId || !date) return undefined;
    return bookings.find((b) => b.vehicleId === vehicleId && b.pickupDate <= date && date <= b.dropoffDate);
  }

  function autoAssign(fine: FineRecord) {
    const b = suggestBooking(fine.vehicleId, fine.date);
    if (!b) return;
    update(fine.id, { clientName: b.customerName, bookingId: b.id });
  }

  const kpis = [
    { icon: Wallet, title: 'Total impayé', value: formatMoney(unpaidTotal, cur), tone: 'text-danger' },
    { icon: Siren, title: 'Contraventions impayées', value: String(unpaid.length), tone: 'text-ink' },
    { icon: BadgeCheck, title: 'Payé ce mois-ci', value: formatMoney(paidThisMonth, cur), tone: 'text-success' },
  ];

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {kpis.map((k) => (
          <div key={k.title} className="flex items-center gap-3 rounded-3xl border border-line bg-card p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-ink text-shell">
              <k.icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted">{k.title}</p>
              <p className={`truncate text-xs font-bold ${k.tone}`}>{k.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-2 rounded-3xl border border-line bg-card p-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-1.5">
          {(['all', 'unpaid', 'paid', 'disputed'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`rounded-xl px-2.5 py-1.5 text-xs font-semibold ${
                statusFilter === s ? 'bg-ink text-shell' : 'border border-line text-muted hover:border-ink'
              }`}
            >
              {s === 'all' ? 'Toutes' : statusFr(s)}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#FF8A00] px-3 py-2 text-xs font-semibold text-slate-950 hover:brightness-95"
        >
          <Plus className="h-3.5 w-3.5" /> Ajouter
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-3xl border border-line bg-card">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr className="bg-ink text-left text-shell">
              <th className="px-3 py-2 font-semibold">Date</th>
              <th className="px-3 py-2 font-semibold">PV N°</th>
              <th className="px-3 py-2 font-semibold">Véhicule</th>
              <th className="px-3 py-2 font-semibold">Motif</th>
              <th className="px-3 py-2 font-semibold">Montant</th>
              <th className="px-3 py-2 font-semibold">Client attribué</th>
              <th className="px-3 py-2 font-semibold">Statut</th>
              <th className="px-3 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted">
                  <Siren className="mx-auto mb-2 h-5 w-5 text-muted" />
                  Aucune contravention.
                </td>
              </tr>
            )}
            {filtered.map((f) => {
              const sug = !f.clientName ? suggestBooking(f.vehicleId, f.date) : undefined;
              return (
                <tr key={f.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2 text-muted">{f.date}</td>
                  <td className="px-3 py-2">
                    <input
                      value={f.pvNumber ?? ''}
                      onChange={(e) => update(f.id, { pvNumber: e.target.value })}
                      placeholder={`PV-${'XXXXX'}`}
                      className="w-24 rounded-lg border border-line bg-shell px-2 py-1 text-xs outline-none focus:border-brand focus:bg-card"
                    />
                  </td>
                  <td className="px-3 py-2 font-semibold text-ink">{vehicleLabel(vehicles, f.vehicleId)}</td>
                  <td className="px-3 py-2 text-muted">{f.label}</td>
                  <td className="px-3 py-2 font-bold text-ink">{formatMoney(f.amount, cur)}</td>
                  <td className="px-3 py-2">
                    {f.clientName ? (
                      <span className="text-ink">
                        {f.clientName}
                        {f.bookingId && <span className="ml-1 text-muted">({f.bookingId})</span>}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted">—</span>
                        {sug && (
                          <button
                            type="button"
                            onClick={() => autoAssign(f)}
                            title={`Réservation ${sug.id} (${sug.customerName})`}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#FF8A00]/10 px-2 py-1 font-semibold text-[#FF8A00] hover:bg-[#FF8A00]/20"
                          >
                            <Zap className="h-3 w-3" /> Attribuer auto
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusChip tone={fineTone(f.status)}>{statusFr(f.status)}</StatusChip>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {f.status !== 'paid' && (
                        <button
                          type="button"
                          onClick={() => update(f.id, { status: 'paid' })}
                          title="Marquer payée"
                          className="rounded-lg border border-line p-1.5 text-success hover:border-success"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {f.status !== 'disputed' && f.status !== 'paid' && (
                        <button
                          type="button"
                          onClick={() => update(f.id, { status: 'disputed' })}
                          title="Contester"
                          className="rounded-lg border border-line p-1.5 text-warning hover:border-warning"
                        >
                          <AlertTriangle className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(f.id)}
                        title="Supprimer"
                        className="rounded-lg border border-line p-1.5 text-muted hover:border-red-300 hover:text-danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border border-line bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-xs font-bold text-ink">Nouvelle contravention</h4>
              <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg p-1 text-muted hover:bg-shell">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Véhicule</label>
                <select value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} className={inputCls}>
                  <option value="">— Choisir —</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} · {v.licensePlate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Date</label>
                  <input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>PV N°</label>
                  <input value={fPv} onChange={(e) => setFPv(e.target.value)} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Motif</label>
                  <select value={fLabel} onChange={(e) => setFLabel(e.target.value)} className={inputCls}>
                    {LABELS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Montant ({currencySymbol(cur)})</label>
                  <input type="number" min="0" value={fAmount} onChange={(e) => setFAmount(e.target.value)} placeholder="300" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Client attribué</label>
                <select value={fClient} onChange={(e) => setFClient(e.target.value)} className={inputCls}>
                  <option value="">— Non attribué —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {(() => {
                  const sug = suggestBooking(fVehicle, fDate);
                  return sug ? (
                    <p className="mt-1 text-muted">
                      Suggestion : {sug.customerName} ({sug.id}){' '}
                      <button
                        type="button"
                        onClick={() => setFClient(sug.customerName)}
                        className="font-bold text-[#FF8A00] hover:underline"
                      >
                        Attribuer auto
                      </button>
                    </p>
                  ) : null;
                })()}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-muted"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={addFine}
                  className="rounded-xl bg-[#FF8A00] px-3 py-2 text-xs font-semibold text-slate-950 hover:brightness-95"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-sm rounded-3xl border border-line bg-card p-5">
            <h4 className="text-xs font-bold text-ink">Supprimer cette contravention ?</h4>
            <p className="mt-1 text-xs text-muted">Cette action est irréversible.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-muted"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  void api(`/api/fines/${confirmDelete}`, { method: 'DELETE' }).then(() => invalidate('fines')).catch(() => {});
                  setConfirmDelete(null);
                }}
                className="rounded-xl bg-danger px-3 py-2 text-xs font-semibold text-white hover:brightness-110"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
